import { mergedFileName, rangeFileName } from '../core/export-name';
import { reelWindows, type ExportLayout, type ExportSegment, type MomentWindow, type SegmentChapter } from '../core/export-plan';
import type { Camera } from '../core/footage';
import { formatDuration, formatShortDate, formatTime, momentLabel, parseClock, placeLabel } from '../core/format';
import { noteTier, reasonTier, SEVERE_BRAKE_MPS2 } from '../core/ranking';
import { wallNow } from '../core/time';
import { utcOffsetOf, type Timeline } from '../core/timeline';
import { entriesOf, type EventEntry, type TriageItem } from '../core/triage';
import type { ExportReply, ExportRequest } from '../worker/protocol';
import { gridOrder } from './grid';
import type { Library, LibraryFile, LibraryFootage } from './library';
import type { EventViewer } from './viewer';

const AROUND_BEFORE_MS = 15_000;
const AROUND_AFTER_MS = 5_000;
const INCIDENT_TIER = 3;
const REEL_NOTE_TIER = 5;
const BYTES_PER_MB = 1024 * 1024;

type Mode = 'window' | 'reel' | 'merge';
type Entry = EventEntry<LibraryFile>;

export interface ExportDom {
  readonly dialog: HTMLDialogElement;
  readonly modes: HTMLElement;
  readonly from: HTMLInputElement;
  readonly to: HTMLInputElement;
  readonly aroundTrigger: HTMLButtonElement;
  readonly whole: HTMLButtonElement;
  readonly reelList: HTMLOListElement;
  readonly mergeInput: HTMLInputElement;
  readonly mergeList: HTMLOListElement;
  readonly hud: HTMLInputElement;
  readonly status: HTMLElement;
  readonly fill: HTMLElement;
  readonly text: HTMLElement;
  readonly problem: HTMLElement;
  readonly download: HTMLAnchorElement;
  readonly cancel: HTMLButtonElement;
  readonly start: HTMLButtonElement;
}

export interface ExportContext {
  readonly library: Library;
  readonly viewer: EventViewer;
  readonly items: readonly TriageItem<LibraryFile>[];
  readonly timelineOf: (footage: LibraryFootage) => Promise<Timeline<LibraryFile> | undefined>;
}

interface Candidate {
  readonly entry: Entry;
  readonly time: number;
  readonly title: string;
  readonly chosen: boolean;
}

interface Job {
  readonly request: ExportRequest;
  readonly name: string;
  readonly started: number;
}

interface Range {
  readonly start: number;
  readonly end: number;
}

const wholeEvent = (viewer: EventViewer): Range => ({ start: viewer.timeline.start, end: viewer.timeline.end });

function aroundTrigger(viewer: EventViewer): Range {
  const anchor = viewer.model.trigger ?? viewer.time;
  return { start: Math.max(viewer.timeline.start, anchor - AROUND_BEFORE_MS), end: Math.min(viewer.timeline.end, anchor + AROUND_AFTER_MS) };
}

function eventSegment(library: Library, timeline: Timeline<LibraryFile>, cameras: readonly Camera[], span: { start: number; end: number }, chapters: readonly SegmentChapter[]): ExportSegment {
  const placed = timeline.slots.flatMap((slot) => slot.clips);
  return {
    start: span.start,
    end: span.end,
    utcOffset: timeline.utcOffset,
    tracks: cameras.map((camera) => ({
      camera,
      clips: placed.filter((entry) => entry.clip.camera === camera).map((entry) => ({ file: entry.clip.ref.file, start: entry.start, span: entry.span })),
    })),
    telemetry: placed.flatMap((entry) => {
      const telemetry = entry.clip.camera === 'front' ? library.scanOf(entry.clip)?.telemetry : undefined;
      return telemetry ? [{ start: entry.start, telemetry }] : [];
    }),
    chapters,
  };
}

const candidateKey = (candidate: Candidate): string => `${candidate.title}|${Math.round(candidate.time / 1000)}`;

function candidatesOf(library: Library, items: readonly TriageItem<LibraryFile>[]): readonly Candidate[] {
  const seen = new Set<string>();
  return allCandidates(library, items).filter((candidate) => !seen.has(candidateKey(candidate)) && Boolean(seen.add(candidateKey(candidate))));
}

function allCandidates(library: Library, items: readonly TriageItem<LibraryFile>[]): readonly Candidate[] {
  return items.flatMap(entriesOf).flatMap((entry) => {
    const place = placeLabel(entry.info);
    const offset = utcOffsetOf(entry.footage.clips, (clip) => library.scanOf(clip));
    const label = (utc: number, what: string): string => `${formatShortDate(utc + offset)} ${formatTime(utc + offset).slice(0, 5)} · ${what} · ${place}`;
    const moments = entry.moments.map((moment) => ({ entry, time: moment.at, title: label(moment.at, momentLabel(moment)), chosen: moment.kind === 'brake' && moment.peak >= SEVERE_BRAKE_MPS2 }));
    const reason = entry.info?.reason;
    const timestamp = entry.info?.timestamp;
    const trigger = reason && timestamp !== undefined && reasonTier(reason) <= INCIDENT_TIER ? [{ entry, time: timestamp - offset, title: label(timestamp - offset, reason.label), chosen: true }] : [];
    const notes = (entry.notes?.moments ?? [])
      .filter((moment) => noteTier(moment) <= REEL_NOTE_TIER)
      .map((moment) => ({ entry, time: moment.peak - offset, title: label(moment.peak - offset, moment.label), chosen: noteTier(moment) <= INCIDENT_TIER }));
    return [...trigger, ...notes, ...moments];
  });
}

export class ExportDialog {
  private worker: Worker | undefined;
  private nextId = 0;
  private job: Job | undefined;
  private mode: Mode = 'window';
  private context: ExportContext | undefined;
  private candidates: Candidate[] = [];
  private mergeFiles: readonly LibraryFile[] = [];
  private downloadUrl: string | undefined;

  constructor(
    private readonly dom: ExportDom,
    private readonly workerUrl: string,
    private readonly fontsUrl: string,
  ) {
    dom.modes.addEventListener('click', (event) => {
      const mode = event.target instanceof Element ? event.target.closest<HTMLElement>('[data-mode]')?.dataset['mode'] : undefined;
      if (mode === 'window' || mode === 'reel' || mode === 'merge') this.setMode(mode);
    });
    dom.aroundTrigger.addEventListener('click', () => this.context && this.setRange(this.context.viewer, aroundTrigger(this.context.viewer)));
    dom.whole.addEventListener('click', () => this.context && this.setRange(this.context.viewer, wholeEvent(this.context.viewer)));
    dom.mergeInput.addEventListener('change', () => {
      this.mergeFiles = [...(dom.mergeInput.files ?? [])].map((file) => ({ path: file.name, size: file.size, lastModified: file.lastModified, file }));
      dom.mergeInput.value = '';
      this.renderMergeList();
    });
    dom.dialog.addEventListener('input', () => this.clearProblem());
    dom.start.addEventListener('click', () => void this.start());
    dom.cancel.addEventListener('click', () => this.cancel());
    dom.dialog.addEventListener('close', () => this.cancel());
  }

  open(context: ExportContext): void {
    this.context = context;
    this.candidates = [...candidatesOf(context.library, context.items)];
    this.renderReelList();
    this.setRange(context.viewer, aroundTrigger(context.viewer));
    this.setMode('window');
    this.showModal();
  }

  openMerge(files: readonly LibraryFile[]): void {
    this.context = undefined;
    this.mergeFiles = [...files].sort((a, b) => a.path.localeCompare(b.path));
    this.renderMergeList();
    this.setMode('merge');
    this.showModal();
  }

  private showModal(): void {
    this.resetStatus();
    this.clearProblem();
    if (!this.dom.dialog.open) this.dom.dialog.showModal();
  }

  private setMode(mode: Mode): void {
    this.clearProblem();
    const available = mode === 'merge' || this.context !== undefined;
    this.mode = available ? mode : 'merge';
    this.dom.modes.querySelectorAll<HTMLElement>('[data-mode]').forEach((tab) => {
      tab.setAttribute('aria-selected', String(tab.dataset['mode'] === this.mode));
      tab.toggleAttribute('disabled', tab.dataset['mode'] !== 'merge' && this.context === undefined);
    });
    this.dom.dialog.querySelectorAll<HTMLElement>('[data-panel]').forEach((panel) => (panel.hidden = panel.dataset['panel'] !== this.mode));
    const hudOption = this.dom.hud.closest('label');
    if (hudOption) hudOption.hidden = this.mode === 'merge';
  }

  private setRange(viewer: EventViewer, range: Range): void {
    this.dom.from.value = formatTime(range.start + viewer.timeline.utcOffset);
    this.dom.to.value = formatTime(range.end + viewer.timeline.utcOffset);
  }

  private readRange(viewer: EventViewer): Range | undefined {
    const timeline = viewer.timeline;
    const reference = timeline.start + timeline.utcOffset;
    const from = this.dom.from.value.trim() ? parseClock(this.dom.from.value, reference) : reference;
    const to = this.dom.to.value.trim() ? parseClock(this.dom.to.value, reference) : timeline.end + timeline.utcOffset;
    if (from === undefined || to === undefined) return undefined;
    const start = Math.max(timeline.start, from - timeline.utcOffset);
    const end = Math.min(timeline.end, to - timeline.utcOffset);
    return end > start ? { start, end } : undefined;
  }

  private renderReelList(): void {
    const rows = this.candidates.map((candidate, index) => {
      const item = document.createElement('li');
      const label = document.createElement('label');
      label.className = 'tv-check';
      const box = document.createElement('input');
      box.type = 'checkbox';
      box.checked = candidate.chosen;
      box.addEventListener('change', () => (this.candidates[index] = { ...candidate, chosen: box.checked }));
      label.append(box, ` ${candidate.title}`);
      item.append(label);
      return item;
    });
    this.dom.reelList.replaceChildren(...rows);
    if (rows.length === 0) this.dom.reelList.append(Object.assign(document.createElement('li'), { className: 'tv-export-text', textContent: 'No moments found yet. Telemetry is still being read, or this footage has none.' }));
  }

  private renderMergeList(): void {
    this.dom.mergeList.replaceChildren(
      ...this.mergeFiles.map((file) => Object.assign(document.createElement('li'), { className: 'tv-export-text', textContent: `${file.path} · ${(file.size / BYTES_PER_MB).toFixed(1)} MB` })),
    );
  }

  private layoutFor(footage: readonly LibraryFootage[]): { layout: ExportLayout; cameras: readonly Camera[] } {
    const focused = this.context?.viewer.focusedCamera;
    if (focused && this.mode === 'window') return { layout: 'single', cameras: [focused] };
    const order = gridOrder(new Set(footage.flatMap((item) => item.clips.map((clip) => clip.camera))));
    return { layout: order.length === 6 ? 'hexa' : 'quad', cameras: order };
  }

  private windowRequest(context: ExportContext, id: number): Job | undefined {
    const range = this.readRange(context.viewer);
    if (!range) return this.problem('Enter a start and end inside the event, as HH:MM:SS.');
    const { viewer, library } = context;
    const { layout, cameras } = this.layoutFor([viewer.footage]);
    const opening = `${placeLabel(library.infoOf(viewer.footage))} · ${formatTime(range.start + viewer.timeline.utcOffset)}`;
    const markers = viewer.model.markers.filter((marker) => marker.time > range.start && marker.time < range.end);
    const chapters = [{ time: range.start, title: opening }, ...markers.map((marker) => ({ time: marker.time, title: marker.label }))];
    const segment = eventSegment(library, viewer.timeline, cameras, range, chapters);
    const request: ExportRequest = { kind: 'render', id, segments: [segment], layout, hud: this.dom.hud.checked, fonts: this.fontsUrl, createdAt: range.start };
    return { request, name: rangeFileName(range.start + viewer.timeline.utcOffset, range.end + viewer.timeline.utcOffset), started: performance.now() };
  }

  private async reelRequest(context: ExportContext, id: number): Promise<Job | undefined> {
    const chosen = this.candidates.filter((candidate) => candidate.chosen);
    if (chosen.length === 0) return this.problem('Tick at least one moment for the reel.');
    this.showProgress(0, 1, 'Reading the clips of the chosen events…');
    const footage = [...new Set(chosen.map((candidate) => candidate.entry.footage))];
    const timelines = new Map(await Promise.all(footage.map(async (item) => [item.id, await context.timelineOf(item)] as const)));
    const windows: MomentWindow[] = chosen.flatMap((candidate) => {
      const timeline = timelines.get(candidate.entry.id);
      if (!timeline) return [];
      const time = Math.min(timeline.end, Math.max(timeline.start, candidate.time));
      return [{ eventId: candidate.entry.id, time, title: candidate.title, eventStart: timeline.start, eventEnd: timeline.end }];
    });
    const { layout, cameras } = this.layoutFor(footage);
    const segments = reelWindows(windows).map((window) => {
      const timeline = timelines.get(window.eventId)!;
      return eventSegment(context.library, timeline, cameras, window, window.chapters);
    });
    const first = segments[0];
    const last = segments.at(-1);
    if (!first || !last) return undefined;
    const request: ExportRequest = { kind: 'render', id, segments, layout, hud: this.dom.hud.checked, fonts: this.fontsUrl, createdAt: first.start };
    return { request, name: rangeFileName(first.start + first.utcOffset, last.end + last.utcOffset), started: performance.now() };
  }

  private mergeRequest(id: number): Job | undefined {
    if (this.mergeFiles.length < 2) return this.problem('Choose at least two videos to join.');
    const files = this.mergeFiles.map((file) => ({ name: file.path.slice(file.path.lastIndexOf('/') + 1), file: file.file }));
    return { request: { kind: 'merge', id, files }, name: mergedFileName(files.map((file) => file.name), wallNow()), started: performance.now() };
  }

  private async start(): Promise<void> {
    if (this.job) return;
    this.clearProblem();
    const id = ++this.nextId;
    const { context } = this;
    const job =
      this.mode === 'merge' || !context ? this.mergeRequest(id) : this.mode === 'window' ? this.windowRequest(context, id) : await this.reelRequest(context, id);
    if (!job) return this.resetStatus();
    this.job = job;
    this.dom.start.disabled = true;
    this.dom.cancel.hidden = false;
    this.showProgress(0, 1, 'Starting…');
    this.workerOnce().postMessage(job.request);
  }

  private workerOnce(): Worker {
    if (this.worker) return this.worker;
    this.worker = new Worker(this.workerUrl, { type: 'module' });
    this.worker.addEventListener('message', (event: MessageEvent<ExportReply>) => this.onReply(event.data));
    this.worker.addEventListener('error', (event) => this.fail(event.message || 'the export worker failed to start'));
    return this.worker;
  }

  private onReply(reply: ExportReply): void {
    const job = this.job;
    if (!job || reply.id !== job.request.id) return;
    if (reply.kind === 'progress') return this.showProgress(reply.done, reply.total, `Rendering frame ${reply.done} of ${reply.total}`);
    if (reply.kind === 'error') return this.fail(reply.message);
    this.finish(job, reply.blob, reply.chapters, reply.seconds);
  }

  private finish(job: Job, blob: Blob, chapters: number, seconds: number): void {
    this.job = undefined;
    if (this.downloadUrl) URL.revokeObjectURL(this.downloadUrl);
    this.downloadUrl = URL.createObjectURL(blob);
    const elapsed = (performance.now() - job.started) / 1000;
    this.dom.download.href = this.downloadUrl;
    this.dom.download.download = job.name;
    this.dom.download.textContent = `Download ${(blob.size / BYTES_PER_MB).toFixed(1)} MB`;
    this.dom.download.hidden = false;
    this.dom.start.disabled = false;
    this.dom.cancel.hidden = true;
    const speed = job.request.kind === 'render' ? ` (${(seconds / Math.max(0.001, elapsed)).toFixed(1)}× real time)` : '';
    this.showProgress(1, 1, `${formatDuration(seconds)} of video with ${chapters} chapter${chapters === 1 ? '' : 's'}, made in ${elapsed.toFixed(1)} s${speed}.`);
  }

  private fail(message: string): void {
    this.job = undefined;
    this.dom.start.disabled = false;
    this.dom.cancel.hidden = true;
    this.showProgress(0, 1, `Export failed: ${message}`);
  }

  private cancel(): void {
    const job = this.job;
    if (!job) return;
    this.worker?.postMessage({ kind: 'cancel', id: job.request.id } satisfies ExportRequest);
    this.job = undefined;
    this.resetStatus();
  }

  private showProgress(done: number, total: number, text: string): void {
    this.dom.status.hidden = false;
    this.dom.fill.style.width = `${(done / Math.max(1, total)) * 100}%`;
    this.dom.text.textContent = text;
  }

  private problem(message: string): undefined {
    this.dom.problem.textContent = message;
    this.dom.problem.hidden = false;
    return undefined;
  }

  private clearProblem(): void {
    this.dom.problem.hidden = true;
  }

  private resetStatus(): void {
    this.dom.status.hidden = true;
    this.dom.download.hidden = true;
    this.dom.cancel.hidden = true;
    this.dom.start.disabled = false;
  }
}
